import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useClient } from '@/api/hooks';
import { useAuth } from '@/contexts/AuthContext';
import { setRuntimeMessageOverrides } from '@/lib/i18n/t';
import {
  DEFAULT_LOCALE_PREFS,
  bumpLocaleListeners,
  getLocalePrefs,
  normalizeCountry,
  normalizeCurrency,
  normalizeDateFormat,
  normalizeTimeFormat,
  normalizeTimezone,
  normalizeUiLocale,
  normalizeWeightUnit,
  setLocalePrefs,
  type LocalePrefs,
  type UiLocale,
} from '@/lib/locale';

type LocaleContextValue = LocalePrefs & {
  /** Prévia local do idioma (Settings) até o perfil salvar no servidor. */
  previewUiLocale: (locale: UiLocale) => void;
};

const LocaleContext = createContext<LocaleContextValue>({
  ...DEFAULT_LOCALE_PREFS,
  previewUiLocale: () => undefined,
});

export function LocaleProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { data: client } = useClient();
  const [uiOverride, setUiOverride] = useState<UiLocale | null>(null);

  const serverUiLocale = normalizeUiLocale(user?.ui_locale);

  useEffect(() => {
    setUiOverride(null);
  }, [serverUiLocale]);

  const prefs = useMemo<LocalePrefs>(() => {
    const country = normalizeCountry(client?.country);
    return {
      uiLocale: uiOverride ?? serverUiLocale,
      country,
      currency: normalizeCurrency(client?.currency, country),
      weightUnit: normalizeWeightUnit(client?.weight_unit),
      dateFormat: normalizeDateFormat(client?.date_format),
      timeFormat: normalizeTimeFormat(client?.time_format),
      timezone: normalizeTimezone(client?.timezone),
    };
  }, [
    uiOverride,
    serverUiLocale,
    client?.country,
    client?.currency,
    client?.weight_unit,
    client?.date_format,
    client?.time_format,
    client?.timezone,
  ]);

  useEffect(() => {
    setLocalePrefs(prefs);
  }, [prefs]);

  useEffect(() => {
    let cancelled = false;
    const locale = prefs.uiLocale;
    fetch(
      `${import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:8000/api/v1'}/ui-messages?locale=${encodeURIComponent(locale)}`,
    )
      .then((r) => r.json())
      .then((body) => {
        if (cancelled || !body?.success) return;
        const messages = (body.data?.messages ?? {}) as Record<string, string>;
        setRuntimeMessageOverrides(locale, messages);
        bumpLocaleListeners();
      })
      .catch(() => {
        /* overrides opcionais */
      });
    return () => {
      cancelled = true;
    };
  }, [prefs.uiLocale]);

  const previewUiLocale = useCallback((locale: UiLocale) => {
    setUiOverride(normalizeUiLocale(locale));
  }, []);

  const value = useMemo<LocaleContextValue>(
    () => ({ ...prefs, previewUiLocale }),
    [prefs, previewUiLocale],
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocalePrefs(): LocalePrefs {
  const ctx = useContext(LocaleContext);
  if (!ctx) return getLocalePrefs();
  const { previewUiLocale: _, ...prefs } = ctx;
  return prefs;
}

export function usePreviewUiLocale() {
  return useContext(LocaleContext).previewUiLocale;
}
